import { FilterOptionType } from '@repo/common/components/Filter';
import { FilterOperator } from '@repo/common/components/Filter/types';
import { isEmpty } from 'lib0/object';
import { useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router';
import type { ISecodaEntity } from '../../api';
import { useCatalogInfiniteList } from '../../api/hooks/resourceCatalog';
import { useCatalogFilter } from '../../components/Filter/useCatalogFilter.ts';
import type { ICommandListItem } from '../../components/Spotlight/components/CommandPalette/constants';
import type { ITableV2Props } from '../../components/TableV2';
import { TableV2 } from '../../components/TableV2';
import { useDataQualityScoreColumns } from '../../components/TableV2/SecodaEntity.hooks';
import type {
	ExtendedDataTableColumn,
	OnCellClickHandlerParams,
} from '../../components/TableV2/types';
import { buildResourceUrl } from '../../utils/navigationUtils';
import { CATALOG_COLUMN_MAPPING } from './CatalogTable.utils.tsx';
import { useActions, useColumns } from './TeamCatalogPage.hooks.tsx';

export function CatalogTable({
	defaultRequiredSearchParams,
	columnVisibility,
	additionalActions,
	...props
}: Partial<ITableV2Props<ISecodaEntity>> & {
	additionalActions?: ICommandListItem<ISecodaEntity>[];
}) {
	const useOverrideColumns = columnVisibility?.catalogServerType
		? (CATALOG_COLUMN_MAPPING[columnVisibility?.catalogServerType]
				.tableV2ColumnsHook ?? (() => []))
		: () => [];

	const genericColumns = useColumns();
	const overrideColumns = useOverrideColumns();
	const dataQualityColumns = useDataQualityScoreColumns({
		catalogType: columnVisibility?.catalogServerType,
	});

	const columns = useMemo(() => {
		const result = (
			!isEmpty(overrideColumns)
				? [...overrideColumns, ...dataQualityColumns]
				: [...genericColumns, ...dataQualityColumns]
		) as ExtendedDataTableColumn<ISecodaEntity>[];
		return result;
	}, [dataQualityColumns, genericColumns, overrideColumns]);

	// eslint-disable-next-line no-underscore-dangle
	const _actions = useActions();

	const actions = useMemo(
		() => [..._actions, ...(additionalActions ?? [])],
		[_actions, additionalActions]
	);

	const navigate = useNavigate();
	// teamId is required to build the fallback catalog URL
	const { teamId } = useParams() as { teamId?: string };

	const handleCellClick = useCallback(
		({ column, record, event }: OnCellClickHandlerParams<ISecodaEntity>) => {
			if (column.navigate !== false) {
				const baseUrl = buildResourceUrl(record);

				// If we have a direct URL, navigate to it.
				if (baseUrl && baseUrl !== '#') {
					if (event instanceof MouseEvent && event.button === 1) {
						window.open(baseUrl, '_blank');
					} else {
						navigate(baseUrl);
					}
					return;
				}

				// No dedicated page -> fallback to catalog with filters (same logic as CatalogTree)
				if (!teamId) {
					// We cannot construct fallback without teamId
					return;
				}

				const filters = encodeURIComponent(
					JSON.stringify([
						{
							value: [record.id],
							operator: FilterOperator.Is,
							filterType: FilterOptionType.PARENT_ID,
						},
					])
				);

				const catalogUrl = `/teams/${teamId}/catalog?filters=${filters}`;

				if (event instanceof MouseEvent && event.button === 1) {
					window.open(catalogUrl, '_blank');
				} else {
					navigate(catalogUrl);
				}
			}
		},
		[navigate, teamId]
	);

	const additionalFilters = useMemo(
		() => [
			'schema',
			'database',
			'sources',
			'schema',
			'related',
			'slack_channels__contains',
		],
		[]
	);

	const withQuickActions = useMemo(
		() => [
			'actions::pii',
			'actions::verified',
			'actions::sidebar',
			'actions::ai',
			'actions::delete',
		],
		[]
	);

	const filterOptions = useCatalogFilter();

	return (
		<TableV2<ISecodaEntity>
			showFilterViews
			pluralTypeString="resources"
			withInfiniteScroll
			usePaginationList={useCatalogInfiniteList}
			withCsvExport
			withServerCsvExport
			withCheckbox
			withSearch
			withInteractiveHeader
			columns={columns}
			withActions={actions}
			withQuickActions={withQuickActions}
			onCellClick={handleCellClick}
			columnVisibility={columnVisibility}
			defaultRequiredSearchParams={defaultRequiredSearchParams}
			additionalFilters={additionalFilters}
			withFilterOptions={filterOptions}
			{...props}
		/>
	);
}
